import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, CheckCircle2, XCircle, AlertTriangle, Rocket, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * Go-Live Wizard — R110.
 *
 * Runs every pre-flight check against the running backend and surfaces
 * a phase-by-phase summary with green/red badges. `ready_for_production`
 * is true only when every CRITICAL check passes; warnings don't block
 * but they discourage.
 */

const PHASE_ICONS = {
  "Security": ShieldCheck,
  "Domains & CORS": Rocket,
  "Payments": Rocket,
  "Integrations": Rocket,
  "Infrastructure": Rocket,
};

function CheckRow({ c }) {
  const Icon = c.ok ? CheckCircle2 : (c.severity === "critical" ? XCircle : AlertTriangle);
  const tone = c.ok
    ? "text-emerald-300"
    : c.severity === "critical" ? "text-rose-300" : "text-amber-300";
  return (
    <div className="flex items-start gap-3 py-3 border-t border-white/5 first:border-t-0" data-testid={`go-live-check-${c.id}`}>
      <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${tone}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-sm text-white">{c.label}</div>
          <Badge variant="outline" className={
            c.severity === "critical"
              ? "border-rose-500/30 text-rose-300 text-[10px] uppercase tracking-widest"
              : "border-amber-500/30 text-amber-300 text-[10px] uppercase tracking-widest"
          }>
            {c.severity}
          </Badge>
        </div>
        <div className={`text-[11px] mt-1 ${c.ok ? "text-zinc-500" : "text-zinc-300"}`}>
          {c.detail}
        </div>
        {!c.ok && c.fix_hint && (
          <div className="text-[11px] mt-1 text-orange-300/80" data-testid={`go-live-fix-${c.id}`}>
            <span className="text-[10px] uppercase tracking-widest text-orange-300/50 mr-1">Fix:</span>
            {c.fix_hint}
          </div>
        )}
      </div>
    </div>
  );
}

export default function GoLivePage() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/system/go-live-checks");
      setState(data);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to run go-live checks");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const totals = state?.totals || {};
  const ready = !!state?.ready_for_production;

  return (
    <div className="p-8 space-y-6" data-testid="go-live-page">
      <PageHeader
        title="Go-Live Wizard"
        subtitle="Every pre-flight check for production cutover. Fix all criticals before flipping the DNS."
        actions={
          <Button variant="outline" onClick={load} className="border-white/10 gap-1 h-9" data-testid="go-live-refresh">
            <RefreshCw className="h-3.5 w-3.5" /> Re-run
          </Button>
        }
      />

      <Card className={`border-2 p-6 ${ready ? "border-emerald-500/40 bg-emerald-500/[0.04]" : "border-rose-500/40 bg-rose-500/[0.04]"}`}
             data-testid="go-live-verdict">
        <div className="flex items-center gap-4">
          {ready
            ? <CheckCircle2 className="h-10 w-10 text-emerald-300 shrink-0" />
            : <XCircle className="h-10 w-10 text-rose-300 shrink-0" />
          }
          <div className="flex-1 min-w-0">
            <div className="text-2xl font-semibold" data-testid="go-live-verdict-label">
              {ready ? "Ready for production" : `${totals.critical_failing || 0} critical issue${(totals.critical_failing || 0) === 1 ? "" : "s"} blocking cutover`}
            </div>
            <div className="text-sm text-zinc-400 mt-1">
              {totals.critical_failing || 0}/{totals.critical || 0} criticals failing ·{" "}
              {totals.warnings_failing || 0}/{totals.warnings || 0} warnings ·{" "}
              {totals.total || 0} checks total
            </div>
          </div>
        </div>
        {!ready && (state?.blocking || []).length > 0 && (
          <div className="mt-4 flex flex-wrap gap-1">
            {(state?.blocking || []).map((b) => (
              <Badge key={b} variant="outline" className="border-rose-500/30 text-rose-300 text-[10px]" data-testid={`go-live-blocking-${b}`}>
                {b}
              </Badge>
            ))}
          </div>
        )}
      </Card>

      {loading || !state ? (
        <div className="p-10 text-center text-sm text-zinc-500">Running checks…</div>
      ) : (
        <div className="space-y-4">
          {Object.entries(state.phases || {}).map(([phaseName, checks]) => {
            const Icon = PHASE_ICONS[phaseName] || Rocket;
            const failing = checks.filter((c) => !c.ok);
            const criticalFail = failing.filter((c) => c.severity === "critical").length;
            return (
              <Card key={phaseName} className="border-white/10 bg-white/[0.02] p-5" data-testid={`go-live-phase-${phaseName.toLowerCase().replaceAll(" ", "-").replaceAll("&", "and")}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="h-4 w-4 text-orange-300" />
                  <div className="text-lg font-semibold">{phaseName}</div>
                  <Badge variant="outline" className={
                    criticalFail > 0
                      ? "border-rose-500/30 text-rose-300 ml-auto"
                      : failing.length > 0
                        ? "border-amber-500/30 text-amber-300 ml-auto"
                        : "border-emerald-500/30 text-emerald-300 ml-auto"
                  }>
                    {criticalFail > 0
                      ? `${criticalFail} critical failing`
                      : failing.length > 0
                        ? `${failing.length} warning${failing.length === 1 ? "" : "s"}`
                        : "all clear"}
                  </Badge>
                </div>
                {checks.map((c) => <CheckRow key={c.id} c={c} />)}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
